import { useEffect, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import type { EmployeeDetail, ImportField } from './types';

// „Angaben ändern“ in der Personalakte (Sam 13.10.: Der Mitarbeiter teilt der Kanzlei z. B. eine neue E-Mail-Adresse
// mit). Alle Felder des Personalfragebogens, geprüft wie beim Übernehmen einer Mitarbeiterliste. Ein leeres Feld löscht
// den Wert. Der Mandant bekommt eine Meldung, welche Angaben geändert wurden.

interface Angaben {
  fields: ImportField[];
  values: Record<string, string | number>;
}

// Überschrift vor diesem Feld
const SECTION: Record<string, string> = {
  vorname: 'Person',
  eintritt: 'Beschäftigung',
  schulabschluss: 'Ausbildung',
  steuerId: 'Steuer',
  svNummer: 'Sozialversicherung',
  iban: 'Bankverbindung',
};
const ISO = /^\d{4}-\d{2}-\d{2}$/;

const shown = (v: string | number) => (typeof v === 'number' ? String(v).replace('.', ',') : v);

// „2.800,50“ → 2800.5; was keine Zahl ist, geht so an den Server (der meldet es)
function toNumber(s: string): number | string {
  const t = s.replace(/\s|€/g, '');
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  return t && Number.isFinite(n) ? n : s;
}

export function EditForm({ e, onCancel, onSaved }: { e: EmployeeDetail; onCancel(): void; onSaved(): Promise<void> }) {
  const toast = useToast();
  const res = useApi<Angaben>(`/api/kanzlei/employee/${e.id}/angaben`);
  const [values, setValues] = useState<Record<string, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (res.data && !values) {
      setValues(Object.fromEntries(Object.entries(res.data.values).map(([k, v]) => [k, shown(v)])));
    }
  }, [res.data, values]);

  if (!res.data || !values) {
    return res.error ? <p className="note emp-err">{res.error}</p> : <div className="spinner" aria-label="Lädt" />;
  }
  const fields = res.data.fields;

  const save = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    setBusy(true);
    const out: Record<string, string | number> = {};
    for (const f of fields) {
      const v = values[f.key]?.trim();
      if (v) out[f.key] = f.kind === 'num' ? toNumber(v) : v;
    }
    try {
      const r = await api.put<{ changed: string[] }>(`/api/kanzlei/employee/${e.id}/angaben`, { values: out });
      toast(r.changed.length ? `Geändert: ${r.changed.join(', ')} · ${e.contactName} sieht es in der App` : 'Nichts geändert');
      await onSaved();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <form className="form emp-ed" onSubmit={save} noValidate aria-label="Angaben ändern">
      {fields.map((f) => {
        const v = values[f.key] ?? '';
        const set = (x: string) => setValues((cur) => ({ ...cur, [f.key]: x }));
        return (
          <div key={f.key} style={{ display: 'contents' }}>
            {SECTION[f.key] && <h4 className="emp-sec">{SECTION[f.key]}</h4>}
            <label>
              {f.label}
              {f.required ? ' *' : ''}
              {f.values ? (
                <select value={f.values.includes(v) ? v : ''} onChange={(x) => set(x.target.value)}>
                  <option value="">–</option>
                  {f.values.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : f.kind === 'date' ? (
                <input type="date" value={ISO.test(v) ? v : ''} max="2100-12-31" onChange={(x) => set(x.target.value)} />
              ) : (
                <input
                  type={f.kind === 'email' ? 'email' : 'text'}
                  inputMode={f.kind === 'num' ? 'decimal' : undefined}
                  value={v}
                  onChange={(x) => set(x.target.value)}
                />
              )}
            </label>
          </div>
        );
      })}
      {error && (
        <p className="note emp-err" role="alert">
          {error}
        </p>
      )}
      <div className="dlg-actions">
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Abbrechen
        </button>
        <button type="submit" className="btn dark" disabled={busy}>
          Speichern
        </button>
      </div>
    </form>
  );
}
